import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  AuditAction,
  AuditLogEntry,
  DEFAULT_SUPER_ADMIN_EMAIL,
  EmailNotificationSettings,
  normalizeEmail,
  StaffRole,
  StaffUser,
} from './types/staffPlatform';

const STAFF_USERS_COLLECTION = 'staff-users';
const AUDIT_LOGS_COLLECTION = 'audit-logs';
const PLATFORM_SETTINGS_COLLECTION = 'platform-settings';
const EMAIL_SETTINGS_DOC = 'email-notifications';

const defaultEmailSettings: EmailNotificationSettings = {
  notificationEmails: [DEFAULT_SUPER_ADMIN_EMAIL],
  notifyOnVendorSubmission: true,
  notifyOnVendorUpdate: true,
  notifyOnVendorApproval: true,
};

function resolveDefaultRole(email: string): StaffRole {
  return normalizeEmail(email) === normalizeEmail(DEFAULT_SUPER_ADMIN_EMAIL)
    ? 'super_admin'
    : 'staff';
}

export async function upsertStaffUserOnLogin(
  email: string,
  name: string
): Promise<{ success: boolean; user?: StaffUser; error?: string }> {
  try {
    const normalizedEmail = normalizeEmail(email);
    const userRef = doc(db, STAFF_USERS_COLLECTION, normalizedEmail);
    const existing = await getDoc(userRef);
    const defaultRole = resolveDefaultRole(normalizedEmail);

    if (existing.exists()) {
      const data = existing.data();
      const updates = {
        name: name || data.name || email,
        lastLoginAt: serverTimestamp(),
        loginCount: (data.loginCount || 0) + 1,
        updatedAt: serverTimestamp(),
        isActive: true,
        ...(normalizedEmail === normalizeEmail(DEFAULT_SUPER_ADMIN_EMAIL) ? { role: 'super_admin' as StaffRole } : {}),
      };

      await updateDoc(userRef, updates);

      const updated = await getDoc(userRef);
      return {
        success: true,
        user: { id: updated.id, ...updated.data() } as StaffUser,
      };
    }

    const newUser = {
      email: normalizedEmail,
      name: name || email,
      role: defaultRole,
      isActive: true,
      loginCount: 1,
      firstLoginAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, newUser);

    return {
      success: true,
      user: { id: normalizedEmail, ...newUser, firstLoginAt: Timestamp.now(), lastLoginAt: Timestamp.now(), createdAt: Timestamp.now(), updatedAt: Timestamp.now() } as StaffUser,
    };
  } catch (error) {
    console.error('Error upserting staff user:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to register staff login',
    };
  }
}

export async function getStaffUser(email: string): Promise<{ success: boolean; user?: StaffUser; error?: string }> {
  try {
    const normalizedEmail = normalizeEmail(email);
    const userRef = doc(db, STAFF_USERS_COLLECTION, normalizedEmail);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      return { success: false, error: 'Staff user not found' };
    }

    return {
      success: true,
      user: { id: snapshot.id, ...snapshot.data() } as StaffUser,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch staff user',
    };
  }
}

export async function getAllStaffUsers(): Promise<{ success: boolean; users?: StaffUser[]; error?: string }> {
  try {
    const q = query(collection(db, STAFF_USERS_COLLECTION), orderBy('lastLoginAt', 'desc'));
    const snapshot = await getDocs(q);

    const users = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as StaffUser[];

    return { success: true, users };
  } catch (error) {
    console.error('Error fetching staff users:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch staff users',
    };
  }
}

export async function updateStaffUserRole(
  targetEmail: string,
  role: StaffRole,
  updatedByEmail: string,
  updatedByName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const normalizedEmail = normalizeEmail(targetEmail);

    if (normalizedEmail === normalizeEmail(DEFAULT_SUPER_ADMIN_EMAIL) && role !== 'super_admin') {
      return { success: false, error: 'The default super admin role cannot be changed.' };
    }

    const userRef = doc(db, STAFF_USERS_COLLECTION, normalizedEmail);
    await updateDoc(userRef, {
      role,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent({
      action: 'staff.role_updated',
      actorEmail: updatedByEmail,
      actorName: updatedByName,
      targetType: 'staff',
      targetId: normalizedEmail,
      targetLabel: normalizedEmail,
      details: `Role updated to ${role}`,
      metadata: { newRole: role },
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update staff role',
    };
  }
}

export async function updateStaffUserStatus(
  targetEmail: string,
  isActive: boolean,
  updatedByEmail: string,
  updatedByName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const normalizedEmail = normalizeEmail(targetEmail);

    if (normalizedEmail === normalizeEmail(DEFAULT_SUPER_ADMIN_EMAIL) && !isActive) {
      return { success: false, error: 'The default super admin cannot be deactivated.' };
    }

    const userRef = doc(db, STAFF_USERS_COLLECTION, normalizedEmail);
    await updateDoc(userRef, {
      isActive,
      updatedAt: serverTimestamp(),
    });

    await logAuditEvent({
      action: 'staff.role_updated',
      actorEmail: updatedByEmail,
      actorName: updatedByName,
      targetType: 'staff',
      targetId: normalizedEmail,
      targetLabel: normalizedEmail,
      details: isActive ? 'Staff account activated' : 'Staff account deactivated',
      metadata: { isActive },
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update staff status',
    };
  }
}

export async function logAuditEvent(params: {
  action: AuditAction;
  actorEmail: string;
  actorName: string;
  targetType?: AuditLogEntry['targetType'];
  targetId?: string;
  targetLabel?: string;
  details?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  try {
    await addDoc(collection(db, AUDIT_LOGS_COLLECTION), {
      ...params,
      actorEmail: normalizeEmail(params.actorEmail),
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Failed to write audit log:', error);
  }
}

export async function getAuditLogs(
  maxResults = 100,
  actionFilter?: AuditAction
): Promise<{ success: boolean; logs?: AuditLogEntry[]; error?: string }> {
  try {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(actionFilter ? 500 : maxResults)
    );

    const snapshot = await getDocs(q);
    let logs = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as AuditLogEntry[];

    if (actionFilter) {
      logs = logs.filter((log) => log.action === actionFilter);
    }

    return { success: true, logs: logs.slice(0, maxResults) };
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch audit logs',
    };
  }
}

export async function getEmailNotificationSettings(): Promise<{
  success: boolean;
  settings?: EmailNotificationSettings;
  error?: string;
}> {
  try {
    const settingsRef = doc(db, PLATFORM_SETTINGS_COLLECTION, EMAIL_SETTINGS_DOC);
    const snapshot = await getDoc(settingsRef);

    if (!snapshot.exists()) {
      await setDoc(settingsRef, {
        ...defaultEmailSettings,
        updatedAt: serverTimestamp(),
        updatedBy: DEFAULT_SUPER_ADMIN_EMAIL,
      });
      return { success: true, settings: defaultEmailSettings };
    }

    return {
      success: true,
      settings: snapshot.data() as EmailNotificationSettings,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch email settings',
    };
  }
}

export async function updateEmailNotificationSettings(
  settings: EmailNotificationSettings,
  updatedByEmail: string,
  updatedByName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const settingsRef = doc(db, PLATFORM_SETTINGS_COLLECTION, EMAIL_SETTINGS_DOC);
    await setDoc(
      settingsRef,
      {
        ...settings,
        notificationEmails: settings.notificationEmails.map(normalizeEmail),
        updatedAt: serverTimestamp(),
        updatedBy: normalizeEmail(updatedByEmail),
      },
      { merge: true }
    );

    await logAuditEvent({
      action: 'settings.email_updated',
      actorEmail: updatedByEmail,
      actorName: updatedByName,
      targetType: 'settings',
      targetId: EMAIL_SETTINGS_DOC,
      targetLabel: 'Email Notification Settings',
      details: `Updated notification emails: ${settings.notificationEmails.join(', ')}`,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update email settings',
    };
  }
}

export function formatAuditAction(action: AuditAction): string {
  const labels: Record<AuditAction, string> = {
    'staff.login': 'Staff Login',
    'staff.logout': 'Staff Logout',
    'staff.role_updated': 'Staff Role Updated',
    'vendor.submitted': 'Vendor Submitted',
    'vendor.updated': 'Vendor Updated',
    'vendor.approved': 'Vendor Approved',
    'vendor.rejected': 'Vendor Rejected',
    'vendor.deleted': 'Vendor Deleted',
    'vendor.sharepoint_sync': 'SharePoint Sync',
    'document.uploaded': 'Document Uploaded',
    'document.deleted': 'Document Deleted',
    'settings.email_updated': 'Email Settings Updated',
  };
  return labels[action] || action;
}

export function formatTimestamp(timestamp: any): string {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString();
}
